import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { apiClient } from '@/api/client'
import { useTeam } from '@/hooks/useTeam'
import { useTeamUsers } from '@/hooks/useTeamUsers'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import type { GameType, Team, Role } from '@foosball/shared'

interface QuickScoreEntryProps {
  gameType: GameType
  targetScore: number
  gameMode: 'team' | 'cross_team' | 'free_play'
}

export function QuickScoreEntry({ gameType, targetScore, gameMode }: QuickScoreEntryProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { getAccessTokenSilently } = useAuth0()
  const { currentTeam } = useTeam()
  const { data: users, isLoading: usersLoading } = useTeamUsers(currentTeam?.id)

  const [redScore, setRedScore] = useState('')
  const [blueScore, setBlueScore] = useState('')
  const [participants, setParticipants] = useState<{
    redDefense?: string
    redAttack?: string
    blueDefense?: string
    blueAttack?: string
  }>({})

  const createGame = useMutation({
    mutationFn: async (gameData: any) => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          // Temporarily remove audience requirement to test
          // audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.post('/games', gameData)
      return response.data
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['games'] })
      queryClient.invalidateQueries({ queryKey: ['team-stats'] })
      queryClient.invalidateQueries({ queryKey: ['player-stats'] })
      queryClient.invalidateQueries({ queryKey: ['global-leaderboard'] })
      queryClient.invalidateQueries({ queryKey: ['global-team-stats'] })
      navigate('/games')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const participantsList: Array<{ user_id: string; team: Team; role: Role }> = []

    if (gameType === '2v2') {
      if (participants.redDefense) participantsList.push({ user_id: participants.redDefense, team: 'red', role: 'defense' })
      if (participants.redAttack) participantsList.push({ user_id: participants.redAttack, team: 'red', role: 'attack' })
      if (participants.blueDefense) participantsList.push({ user_id: participants.blueDefense, team: 'blue', role: 'defense' })
      if (participants.blueAttack) participantsList.push({ user_id: participants.blueAttack, team: 'blue', role: 'attack' })
    } else {
      if (participants.redDefense) participantsList.push({ user_id: participants.redDefense, team: 'red', role: 'full' })
      if (participants.blueDefense) participantsList.push({ user_id: participants.blueDefense, team: 'blue', role: 'full' })
    }

    createGame.mutate({
      team_id: currentTeam?.id,
      game_type: gameType,
      game_mode: gameMode,
      target_score: targetScore,
      participants: participantsList,
      quick_entry: true,
      final_scores: {
        red: parseInt(redScore),
        blue: parseInt(blueScore),
      },
    })
  }

  if (usersLoading) {
    return <LoadingSpinner size="lg" />
  }

  const approvedUsers = users?.filter((u: any) => u.status === 'approved') || []

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">Quick Entry Mode</h3>
        <p className="text-sm text-blue-700">
          Select players and enter final scores. Goal details won't be tracked.
        </p>
      </div>

      {/* Player Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Select Players</h3>

        {gameType === '2v2' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Red Team */}
            <div className="space-y-4">
              <h4 className="font-medium text-red-700">Red Team</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Defense
                </label>
                <select
                  value={participants.redDefense || ''}
                  onChange={(e) => setParticipants({ ...participants, redDefense: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                >
                  <option value="">Select player...</option>
                  {approvedUsers.map((user: any) => (
                    <option key={user.id} value={user.id}>
                      {user.display_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Attack
                </label>
                <select
                  value={participants.redAttack || ''}
                  onChange={(e) => setParticipants({ ...participants, redAttack: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                >
                  <option value="">Select player...</option>
                  {approvedUsers.map((user: any) => (
                    <option key={user.id} value={user.id}>
                      {user.display_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Blue Team */}
            <div className="space-y-4">
              <h4 className="font-medium text-blue-700">Blue Team</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Defense
                </label>
                <select
                  value={participants.blueDefense || ''}
                  onChange={(e) => setParticipants({ ...participants, blueDefense: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                >
                  <option value="">Select player...</option>
                  {approvedUsers.map((user: any) => (
                    <option key={user.id} value={user.id}>
                      {user.display_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Attack
                </label>
                <select
                  value={participants.blueAttack || ''}
                  onChange={(e) => setParticipants({ ...participants, blueAttack: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                >
                  <option value="">Select player...</option>
                  {approvedUsers.map((user: any) => (
                    <option key={user.id} value={user.id}>
                      {user.display_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Red Player (Full Table)
              </label>
              <select
                value={participants.redDefense || ''}
                onChange={(e) => setParticipants({ ...participants, redDefense: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              >
                <option value="">Select player...</option>
                {approvedUsers.map((user: any) => (
                  <option key={user.id} value={user.id}>
                    {user.display_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Blue Player (Full Table)
              </label>
              <select
                value={participants.blueDefense || ''}
                onChange={(e) => setParticipants({ ...participants, blueDefense: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              >
                <option value="">Select player...</option>
                {approvedUsers.map((user: any) => (
                  <option key={user.id} value={user.id}>
                    {user.display_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Score Entry */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Final Scores</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-red-700 mb-1">
              Red Score
            </label>
            <input
              type="number"
              min="-10"
              max="50"
              value={redScore}
              onChange={(e) => setRedScore(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-blue-700 mb-1">
              Blue Score
            </label>
            <input
              type="number"
              min="-10"
              max="50"
              value={blueScore}
              onChange={(e) => setBlueScore(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={() => navigate('/games')}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={createGame.isPending}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
        >
          {createGame.isPending ? 'Saving...' : 'Save Game'}
        </button>
      </div>
    </form>
  )
}
